// src/components/VehicleList.jsx
import { useEffect, useState, useMemo } from 'react';
import {
  Table,
  ScrollArea,
  Badge,
  Group,
  Text,
  ActionIcon,
  LoadingOverlay,
  Paper,
  Button,
  Tooltip,
  Modal,
  MultiSelect,
  TextInput,
  Stack,
  Flex,
  ThemeIcon,
  Divider,
  UnstyledButton,
  SimpleGrid,
  Title,
  Box,
  Center,
} from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconPlus,
  IconEye,
  IconAlertTriangle,
  IconSearch,
  IconFileText,
  IconCar,
  IconMapPin,
  IconInfoCircle,
  IconDownload,
  IconSelector,
  IconChevronUp,
  IconChevronDown,
  IconTruck,
  IconClockHour4,
  IconFilter,
  IconCheck,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import api from '../../api/axiosConfig';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

import AddVehicleModal from './AddVehicleModal';
import VehicleDetailModal from './VehicleDetailModal';
import VehicleDocumentsModal from './VehicleDocumentsModal';

export default function VehicleList() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);

  const [vehicleTypeFilter, setVehicleTypeFilter] = useState([]);

  const [globalSearch, setGlobalSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleToEdit, setVehicleToEdit] = useState(null);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  const [addModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false);
  const [detailModalOpened, { open: openDetailModal, close: closeDetailModal }] =
    useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);
  const [docsModalOpened, { open: openDocsModal, close: closeDocsModal }] =
    useDisclosure(false);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const response = await api.get('/vehicles/');
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Araç listesi çekilemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const response = await api.get('/vehicles/vehicle-types/');
      const formattedOptions = (response.data || []).map((item) => ({
        value: item.name,
        label: item.name,
      }));
      setVehicleTypeOptions(formattedOptions);
    } catch (error) {
      console.error('Araç tipleri çekilemedi:', error);
    }
  };

  useEffect(() => {
    fetchVehicles();
    fetchVehicleTypes();
  }, []);

  const filteredVehicles = useMemo(() => {
    return vehicles.filter((vehicle) => {
      const matchesVehicleType =
        vehicleTypeFilter.length === 0 ||
        vehicleTypeFilter.includes(vehicle.vehicle_type);

      const searchTerm = globalSearch.toLocaleLowerCase('tr-TR');
      const matchesSearch = searchTerm
        ? vehicle.plate?.toLocaleLowerCase('tr-TR').includes(searchTerm) ||
          vehicle.ownership?.toLocaleLowerCase('tr-TR').includes(searchTerm) ||
          vehicle.brand?.toLocaleLowerCase('tr-TR').includes(searchTerm) ||
          vehicle.current_location?.toLocaleLowerCase('tr-TR').includes(searchTerm) ||
          vehicle.allocated_to?.toLocaleLowerCase('tr-TR').includes(searchTerm) ||
          vehicle.model_year?.toString().toLocaleLowerCase('tr-TR').includes(searchTerm) ||
          vehicle.casco_company?.toLocaleLowerCase('tr-TR').includes(searchTerm) ||
          vehicle.phone?.toLocaleLowerCase('tr-TR').includes(searchTerm) ||
          vehicle.commercial_type?.toLocaleLowerCase('tr-TR').includes(searchTerm)
        : true;

      return matchesVehicleType && matchesSearch;
    });
  }, [vehicles, vehicleTypeFilter, globalSearch]);

  const sortedVehicles = useMemo(() => {
    const sortableItems = [...filteredVehicles];

    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (!aValue) return 1;
        if (!bValue) return -1;

        const dateA = dayjs(aValue).valueOf();
        const dateB = dayjs(bValue).valueOf();

        if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return sortableItems;
  }, [filteredVehicles, sortConfig]);

  // DÜZELTME: İstatistikler artık `vehicles` dizisinden değil, arama sonuçlarını barındıran `filteredVehicles` dizisinden hesaplanıyor.
  const summaryStats = useMemo(() => {
    const total = filteredVehicles.length;
    const ownVehicles = filteredVehicles.filter((v) => v.ownership === 'Öz Mal').length;
    const rentedVehicles = filteredVehicles.filter((v) => v.ownership && v.ownership !== 'Öz Mal').length;
    const allocatedCount = filteredVehicles.filter((v) => v.allocated_to).length;

    return {
      total,
      ownVehicles,
      rentedVehicles,
      allocatedCount,
    };
  }, [filteredVehicles]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const formatDate = (date) => (date ? dayjs(date).format('DD.MM.YYYY') : '-');

  const getDateStatus = (date) => {
    if (!date) return { label: '-', color: 'gray', variant: 'light' };

    const diff = dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day');

    if (diff < 0) {
      return { label: `${Math.abs(diff)} gün geçti`, color: 'red', variant: 'filled' };
    }
    if (diff <= 30) {
      return { label: `${diff} gün kaldı`, color: 'orange', variant: 'light' };
    }
    return { label: `${diff} gün kaldı`, color: 'green', variant: 'light' };
  };

  const handleExportToExcel = () => {
    const excelData = sortedVehicles.map((v) => {
      const formattedData = {
        'Ruhsat No': v.document_no || '-',
        Plaka: v.plate || '-',
        'Araç Cinsi': v.vehicle_type || '-',
        Marka: v.brand || '-',
        'Ticari Tip': v.commercial_type || '-',
        'Model Yılı': v.model_year || '-',
        Mülkiyet: v.ownership || '-',
        'Güncel Lokasyon': v.current_location || 'Belirtilmemiş',
        'Tahsis Edilen Kişi': v.allocated_to || 'Tahsis Yok',
        'Telefon': v.phone || 'Telefon Yok',
        Kilometre: v.current_km || 0,
        'Tescil Tarihi': formatDate(v.registration_date),
        'Yakıt Türü': v.fuel_type || '-',
        'Trafik Sigortası Bitiş': formatDate(v.traffic_insurance_expiry),
        'Kasko Bitiş': formatDate(v.casco_expiry),
        'Muayene Bitiş': formatDate(v.inspection_expiry_date),
        'Trafik Sigorta Firması': v.traffic_insurance_company || '-',
        'Trafik Sigorta No': v.traffic_insurance_policy_no || '-',
        'Kasko Firması': v.casco_company || '-',
        'Kasko No': v.casco_policy_no || '-',
      };

      const knownKeys = [
        'id',
        'plate',
        'commercial_type',
        'brand',
        'vehicle_type',
        'model_year',
        'traffic_insurance_company',
        'traffic_insurance_policy_no',
        'ownership',
        'current_location',
        'allocated_to',
        'phone',
        'current_km',
        'casco_company',
        'casco_policy_no',
        'fuel_type',
        'registration_date',
        'traffic_insurance_expiry',
        'casco_expiry',
        'inspection_expiry_date',
        'document_no',
      ];

      Object.keys(v).forEach((key) => {
        if (!knownKeys.includes(key) && typeof v[key] !== 'object') {
          formattedData[key] = v[key] !== null ? v[key] : '-';
        }
      });

      return formattedData;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const colWidths = Object.keys(excelData[0] || {}).map(() => ({ wch: 20 }));
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Araç Listesi');

    XLSX.writeFile(workbook, `Arac_Envanteri_${dayjs().format('DD_MM_YYYY')}.xlsx`);
  };

  const handleAddClick = () => {
    setVehicleToEdit(null);
    openAddModal();
  };

  const handleEditClick = (vehicle) => {
    setVehicleToEdit(vehicle);
    openAddModal();
  };

  const handleDeleteClick = (id) => {
    setVehicleToDelete(id);
    openDeleteModal();
  };

  const confirmDelete = async () => {
    if (!vehicleToDelete) return;

    try {
      await api.delete(`/vehicles/${vehicleToDelete}`);
      await fetchVehicles();
      closeDeleteModal();
      setVehicleToDelete(null);
    } catch (error) {
      console.error('Silme hatası', error);
      alert('Silme işlemi sırasında bir hata oluştu.');
    }
  };

  const handleViewDetails = (vehicle) => {
    setSelectedVehicle(vehicle);
    openDetailModal();
  };

  const handleDocsClick = (vehicle) => {
    setSelectedVehicle(vehicle);
    openDocsModal();
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <IconSelector size={14} color="gray" />;
    }
    return sortConfig.direction === 'asc' ? (
      <IconChevronUp size={14} />
    ) : (
      <IconChevronDown size={14} />
    );
  };

  const rows = sortedVehicles.map((vehicle) => {
    const insuranceStatus = getDateStatus(vehicle.traffic_insurance_expiry);
    const cascoStatus = getDateStatus(vehicle.casco_expiry);
    const inspectionStatus = getDateStatus(vehicle.inspection_expiry_date);

    return (
      <Table.Tr key={vehicle.id}>
        <Table.Td style={{ whiteSpace: 'nowrap' }}>
          <Badge
            size="lg"
            variant="light"
            color="dark"
            radius="md"
            styles={{
              label: {
                fontWeight: 800,
                letterSpacing: '0.3px',
                overflow: 'visible',
                textOverflow: 'clip',
              },
            }}
          >
            {vehicle.plate}
          </Badge>
        </Table.Td>

        <Table.Td>
          <Stack gap={2}>
            <Text size="sm" fw={700}>
              {vehicle.brand || '-'}
            </Text>
            <Text size="xs" c="dimmed">
              {vehicle.commercial_type || '-'}
            </Text>
          </Stack>
        </Table.Td>

        <Table.Td>
          <Text size="sm" fw={500}>
            {vehicle.model_year || '-'}
          </Text>
        </Table.Td>

        <Table.Td>
          <Text size="sm">{formatDate(vehicle.registration_date)}</Text>
        </Table.Td>

        <Table.Td>
          <Badge
            color={vehicle.ownership === 'Öz Mal' ? 'teal' : 'indigo'}
            variant="light"
            radius="xl"
          >
            {vehicle.ownership || '-'}
          </Badge>
        </Table.Td>

        <Table.Td>
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <ThemeIcon variant="light" color="gray" radius="xl" size="sm">
              <IconMapPin size={12} />
            </ThemeIcon>
            <div>
              <Text size="sm" fw={600}>
                {vehicle.current_location || 'Belirtilmemiş'}
              </Text>
              <Text size="xs" c="dimmed">
                {vehicle.allocated_to || 'Tahsis Yok'}
              </Text>
              <Text size="xs" c="dimmed">
                {vehicle.phone || 'Telefon Yok'}
              </Text>
            </div>
          </Group>
        </Table.Td>

        <Table.Td style={{ textAlign: 'right' }}>
          <Text size="sm" fw={700}>
            {vehicle.current_km ? `${vehicle.current_km.toLocaleString()} km` : '-'}
          </Text>
        </Table.Td>

        <Table.Td>
          <Stack gap={4}>
            <Text size="sm">{formatDate(vehicle.traffic_insurance_expiry)}</Text>
            {vehicle.traffic_insurance_expiry && (
              <Badge
                size="xs"
                radius="xl"
                color={insuranceStatus.color}
                variant={insuranceStatus.variant}
                w="fit-content"
              >
                {insuranceStatus.label}
              </Badge>
            )}
          </Stack>
        </Table.Td>

        <Table.Td>
          <Stack gap={4}>
            <Text size="sm">{formatDate(vehicle.casco_expiry)}</Text>
            {vehicle.casco_expiry && (
              <Badge
                size="xs"
                radius="xl"
                color={cascoStatus.color}
                variant={cascoStatus.variant}
                w="fit-content"
              >
                {cascoStatus.label}
              </Badge>
            )}
          </Stack>
        </Table.Td>

        <Table.Td>
          <Stack gap={4}>
            <Text size="sm">{formatDate(vehicle.inspection_expiry_date)}</Text>
            {vehicle.inspection_expiry_date && (
              <Badge
                size="xs"
                radius="xl"
                color={inspectionStatus.color}
                variant={inspectionStatus.variant}
                w="fit-content"
              >
                {inspectionStatus.label}
              </Badge>
            )}
          </Stack>
        </Table.Td>

        <Table.Td>
          <Group gap={6} justify="flex-end" wrap="nowrap">
            <Tooltip label="Detayları Gör" withArrow position="top">
              <ActionIcon
                variant="light"
                color="teal"
                radius="xl"
                size="lg"
                onClick={() => handleViewDetails(vehicle)}
              >
                <IconEye size={17} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Belgeler" withArrow position="top">
              <ActionIcon
                variant="light"
                color="violet"
                radius="xl"
                size="lg"
                onClick={() => handleDocsClick(vehicle)}
              >
                <IconFileText size={17} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Düzenle" withArrow position="top">
              <ActionIcon
                variant="light"
                color="blue"
                radius="xl"
                size="lg"
                onClick={() => handleEditClick(vehicle)}
              >
                <IconEdit size={17} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Sil" withArrow position="top">
              <ActionIcon
                variant="light"
                color="red"
                radius="xl"
                size="lg"
                onClick={() => handleDeleteClick(vehicle.id)}
              >
                <IconTrash size={17} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Stack gap="lg" pos="relative" style={{ minHeight: '70vh' }}>
      <LoadingOverlay visible={loading} overlayProps={{ radius: 'sm', blur: 2 }} />

      {/* HERO */}
      <Paper
        radius="xl"
        p={{ base: 'lg', md: 'xl' }}
        style={{
          position: 'relative',
          overflow: 'hidden',
          background:
            'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 40%, rgba(37,99,235,0.88) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
        }}
      >
        <Box
          style={{
            position: 'absolute',
            top: -70,
            right: -70,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
          }}
        />
        <Box
          style={{
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'rgba(96,165,250,0.18)',
          }}
        />

        <Flex
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          wrap="wrap"
          gap="md"
          style={{ position: 'relative', zIndex: 1 }}
        >
          <Group align="flex-start" wrap="nowrap">
            <ThemeIcon
              size={54}
              radius="xl"
              variant="black"
              color="rgba(255,255,255,0.12)"
            >
              <IconTruck size={28} color="white" />
            </ThemeIcon>

            <div>
              <Text fw={700} size="sm" c="rgba(255,255,255,0.75)">
                Araç Filosu Yönetimi
              </Text>
              <Title order={2} c="white" fw={900} style={{ letterSpacing: '-0.7px' }}>
                Araç Envanter Listesi
              </Title>
              <Text
                size="sm"
                maw={720}
                style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.7 }}
              >
                Filodaki araçları filtreleyebilir, sıralayabilir, detaylarını
                görüntüleyebilir ve belge süreçlerini tek ekrandan
                yönetebilirsiniz.
              </Text>
            </div>
          </Group>

          <Paper
            radius="xl"
            p="md"
            style={{
              minWidth: 240,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <Group wrap="nowrap">
              <ThemeIcon
                size={42}
                radius="xl"
                variant="black"
                color="rgba(255,255,255,0.14)"
              >
                <IconClockHour4 size={20} color="white" />
              </ThemeIcon>

              <div>
                <Text size="xs" fw={700} c="rgba(255,255,255,0.70)">
                  Güncel Kayıt
                </Text>
                <Text fw={800} c="white">
                  {sortedVehicles.length} araç listeleniyor
                </Text>
              </div>
            </Group>
          </Paper>
        </Flex>
      </Paper>

      {/* ÖZET KARTLAR */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
        <Paper
          radius="xl"
          p="lg"
          withBorder
          style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 8px 24px rgba(15,23,42,0.05)',
          }}
        >
          <Group justify="space-between">
            <div>
              <Text size="sm" c="dimmed" fw={600}>
                Toplam Araç
              </Text>
              <Title order={2} fw={900}>
                {summaryStats.total}
              </Title>
            </div>
            <ThemeIcon size={52} radius="xl" variant="light" color="blue">
              <IconCar size={26} />
            </ThemeIcon>
          </Group>
        </Paper>

        <Paper
          radius="xl"
          p="lg"
          withBorder
          style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 8px 24px rgba(15,23,42,0.05)',
          }}
        >
          <Group justify="space-between">
            <div>
              <Text size="sm" c="dimmed" fw={600}>
                Tahsisli Araç
              </Text>
              <Title order={2} fw={900}>
                {summaryStats.allocatedCount}
              </Title>
            </div>
            <ThemeIcon size={52} radius="xl" variant="light" color="violet">
              <IconMapPin size={26} />
            </ThemeIcon>
          </Group>
        </Paper>
      </SimpleGrid>

      {/* FİLTRE / AKSİYON */}
      <Paper
        radius="xl"
        withBorder
        p="md"
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
        }}
      >
        <Stack gap="md">
          <Flex justify="space-between" align="center" wrap="wrap" gap="md">
            <div>
              <Title order={4} fw={800}>
                Araç Listesi
              </Title>
              <Text size="sm" c="dimmed">
                Filtreleme, sıralama ve işlem yönetimi
              </Text>
            </div>

            <Group>
              <Button
                variant="light"
                color="green"
                leftSection={<IconDownload size={18} />}
                onClick={handleExportToExcel}
                radius="xl"
                size="md"
                disabled={sortedVehicles.length === 0}
              >
                Excel'e Aktar
              </Button>

              <Button
                leftSection={<IconPlus size={18} />}
                onClick={handleAddClick}
                radius="xl"
                size="md"
              >
                Yeni Araç Ekle
              </Button>
            </Group>
          </Flex>

          <Divider />

          <Paper
            radius="lg"
            p="md"
            style={{
              background: 'var(--mantine-color-gray-0)',
              border: '1px solid var(--mantine-color-gray-2)',
            }}
          >
            <Group grow align="flex-end">
              <TextInput
                label="Gelişmiş Arama"
                placeholder="Plaka, marka, lokasyon, kişi veya tip ara..."
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.currentTarget.value)}
                leftSection={<IconSearch size={16} />}
                radius="xl"
              />

              <MultiSelect
                label="Araç Cinsi"
                placeholder="Tüm Araçlar"
                data={vehicleTypeOptions}
                value={vehicleTypeFilter}
                onChange={setVehicleTypeFilter}
                leftSection={<IconFilter size={16} />}
                searchable
                clearable
                radius="xl"
              />
            </Group>
          </Paper>
        </Stack>
      </Paper>

      {/* MODALLAR */}
      <AddVehicleModal
        opened={addModalOpened}
        close={closeAddModal}
        onVehicleAdded={fetchVehicles}
        vehicleToEdit={vehicleToEdit}
      />

      <VehicleDetailModal
        opened={detailModalOpened}
        close={closeDetailModal}
        vehicle={selectedVehicle}
      />

      <VehicleDocumentsModal
        opened={docsModalOpened}
        close={closeDocsModal}
        vehicle={selectedVehicle}
      />

      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        radius="xl"
        overlayProps={{ blur: 3 }}
        title={
          <Group gap="sm">
            <ThemeIcon color="red" variant="light" radius="xl" size="md">
              <IconAlertTriangle size={18} />
            </ThemeIcon>
            <Text fw={800} size="lg">
              Silme Onayı
            </Text>
          </Group>
        }
      >
        <Stack gap="lg">
          <Paper
            p="md"
            radius="lg"
            style={{
              background: 'var(--mantine-color-red-0)',
              border: '1px solid var(--mantine-color-red-2)',
            }}
          >
            <Text size="sm">
              Bu aracı sistemden silmek istediğinize emin misiniz? Bu araca ait
              tüm veriler kalıcı olarak kaldırılacaktır.
            </Text>
          </Paper>

          <Group justify="flex-end">
            <Button variant="default" radius="xl" onClick={closeDeleteModal}>
              Vazgeç
            </Button>
            <Button
              color="red"
              radius="xl"
              onClick={confirmDelete}
              leftSection={<IconTrash size={16} />}
            >
              Evet, Sil
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* TABLO */}
      <Paper
        radius="xl"
        withBorder
        style={{
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
        }}
      >
        <ScrollArea h="65vh" type="auto" offsetScrollbars>
          <Table
            stickyHeader
            stickyHeaderOffset={0}
            striped
            highlightOnHover
            verticalSpacing="md"
            horizontalSpacing="md"
            style={{ minWidth: 1350 }}
          >
            <Table.Thead
              style={{
                background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
              }}
            >
              <Table.Tr>
                <Table.Th>Plaka</Table.Th>
                <Table.Th>Marka / Tip</Table.Th>
                <Table.Th>Model</Table.Th>
                <Table.Th>Tescil Tar.</Table.Th>
                <Table.Th>Mülkiyet</Table.Th>
                <Table.Th>Lokasyon / Tahsis</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Kilometre</Table.Th>

                <Table.Th>
                  <UnstyledButton
                    onClick={() => requestSort('traffic_insurance_expiry')}
                    p={4}
                    style={{ borderRadius: 6 }}
                  >
                    <Group gap={5}>
                      <Text size="sm" fw={700}>
                        Trafik
                      </Text>
                      <SortIcon columnKey="traffic_insurance_expiry" />
                    </Group>
                  </UnstyledButton>
                </Table.Th>

                <Table.Th>
                  <UnstyledButton
                    onClick={() => requestSort('casco_expiry')}
                    p={4}
                    style={{ borderRadius: 6 }}
                  >
                    <Group gap={5}>
                      <Text size="sm" fw={700}>
                        Kasko
                      </Text>
                      <SortIcon columnKey="casco_expiry" />
                    </Group>
                  </UnstyledButton>
                </Table.Th>

                <Table.Th>
                  <UnstyledButton
                    onClick={() => requestSort('inspection_expiry_date')}
                    p={4}
                    style={{ borderRadius: 6 }}
                  >
                    <Group gap={5}>
                      <Text size="sm" fw={700}>
                        Muayene
                      </Text>
                      <SortIcon columnKey="inspection_expiry_date" />
                    </Group>
                  </UnstyledButton>
                </Table.Th>

                <Table.Th style={{ textAlign: 'right' }}>İşlemler</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>{rows}</Table.Tbody>
          </Table>

          {sortedVehicles.length === 0 && !loading && (
            <Center py={70}>
              <Stack align="center" gap="sm">
                <ThemeIcon size={72} radius="xl" variant="light" color="gray">
                  <IconInfoCircle size={34} />
                </ThemeIcon>
                <Title order={4}>Araç bulunamadı</Title>
                <Text c="dimmed" size="sm" ta="center" maw={420}>
                  Arama kriterlerine uygun araç bulunamadı. Farklı bir arama
                  terimi deneyebilir veya filtreleri temizleyebilirsiniz.
                </Text>
              </Stack>
            </Center>
          )}
        </ScrollArea>
      </Paper>
    </Stack>
  );
}